/**
 * The Studio's transcript list: search box + every cue as one clickable
 * line. Clicking selects the cue and seeks to it, which is how someone fixes
 * a caption they can hear but cannot find on the timeline.
 *
 * Direction is explicit (`dir`, from the channel's brand): an Arabic
 * transcript rendered in the app's LTR flow left-aligns the text and puts
 * the ellipsis at the START of the sentence — the half a reader needs.
 */
import { Search } from "lucide-react";

import type { SerializableCue } from "../../../../../src/remotion/types.ts";
import { labelSnippet } from "../../../lib/overlaysEdit.ts";
import { fieldLabelStyle } from "../../styles.ts";

export function TranscriptList({ matches, query, onQueryChange, selectedIdx, onSelectCue, dir }: {
  /** Cues that match the query, carrying their ORIGINAL index. */
  matches: Array<{ c: SerializableCue; i: number }>;
  query: string;
  onQueryChange: (q: string) => void;
  selectedIdx: number | null;
  onSelectCue: (i: number) => void;
  dir: "rtl" | "ltr";
}): React.JSX.Element {
  return (
    <div style={{ display: "grid", gap: 7, minWidth: 0 }}>
      <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>Pick a caption</span>
      <div dir={dir} style={{ position: "relative", minWidth: 0 }}>
        <Search
          size={12}
          style={{ position: "absolute", insetInlineStart: 9, top: "50%", transform: "translateY(-50%)", color: "var(--ql-fg-subtle)" }}
        />
        <input
          type="text"
          value={query}
          dir={dir}
          placeholder="Search the transcript"
          aria-label="Search captions"
          onChange={(e) => onQueryChange(e.target.value)}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "7px 9px",
            paddingInlineStart: 26,
            borderRadius: 8,
            border: "1px solid var(--ql-border)",
            background: "rgba(255,255,255,.03)",
            color: "var(--ql-fg)",
            fontFamily: "inherit",
            fontSize: 12,
            outline: "none",
          }}
        />
      </div>
      <div style={{ display: "grid", gap: 3, maxHeight: 220, overflowY: "auto", minWidth: 0 }}>
        {matches.length === 0 && (
          <span style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)" }}>No caption matches “{query.trim()}”.</span>
        )}
        {matches.map(({ c, i }) => {
          const active = i === selectedIdx;
          return (
            <button
              key={`${c.startMs}-${i}`}
              type="button"
              dir={dir}
              onClick={() => onSelectCue(i)}
              title={c.text.replace(/\n/g, " ")}
              style={{
                display: "flex",
                gap: 7,
                alignItems: "baseline",
                textAlign: "start",
                padding: "6px 8px",
                borderRadius: 7,
                cursor: "pointer",
                fontFamily: "inherit",
                minWidth: 0,
                border: "1px solid " + (active ? "var(--ql-yellow)" : "transparent"),
                background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
                color: active ? "var(--ql-fg)" : "var(--ql-fg-muted)",
              }}
            >
              <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 10.5, color: "var(--ql-fg-subtle)", flexShrink: 0 }}>
                {i + 1}
              </span>
              <span style={{ fontSize: 12, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {labelSnippet(c.text, 60)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
